// IoT API Service
import axios from 'axios';
import {
  IotDoorControlRequest,
  IotDoorControlResponse,
  IotLoginRequest,
  IotLoginResponse,
  IotDevicesResponse,
} from '../types';

const IOT_BASE_URL = 'https://icadm.megazo.io/api';
const IOT_TOKEN_KEY = 'iot_auth_token';

// Hardcoded credentials
const HARDCODED_CREDENTIALS = {
  username: 'ccMuser1',
  password: 'rubka@12qozhiN',
};

interface TokenRefreshResponse {
  code: number;
  msg: string;
  data: string; // The new token
}

class IotApiService {
  private refreshPromise: Promise<string> | null = null;

  private getToken(): string | null {
    return localStorage.getItem(IOT_TOKEN_KEY);
  }

  private setToken(token: string): void {
    localStorage.setItem(IOT_TOKEN_KEY, token);
  }

  private clearToken(): void {
    localStorage.removeItem(IOT_TOKEN_KEY);
  }

  async login(credentials: IotLoginRequest): Promise<IotLoginResponse> {
    try {
      const response = await axios.post<IotLoginResponse>(`${IOT_BASE_URL}/user/login`, credentials);

      if (response.data.code === 0 && response.data.data.token) {
        this.setToken(response.data.data.token);
      }

      return response.data;
    } catch (error) {
      console.error('IoT login error:', error);
      throw error;
    }
  }

  async ensureAuthenticated(): Promise<string> {
    let token = this.getToken();

    if (!token) {
      // Auto-login with hardcoded credentials
      console.log('🔐 Auto-logging in to IoT service...');
      const response = await this.login(HARDCODED_CREDENTIALS);
      token = response.data.token;
    }

    return token;
  }

  async refreshToken(): Promise<string> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const currentToken = this.getToken();

        if (!currentToken) {
          // No token to refresh, perform fresh login
          const response = await this.login(HARDCODED_CREDENTIALS);
          return response.data.token;
        }

        console.log('🔄 Refreshing IoT token...');
        const response = await axios.get<TokenRefreshResponse>(`${IOT_BASE_URL}/user/token/refresh`, {
          headers: {
            token: currentToken,
          },
        });

        if (response.data.code === 0 && response.data.data) {
          const newToken = response.data.data;
          this.setToken(newToken);
          console.log('✅ IoT token refreshed successfully');
          return newToken;
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (error) {
        console.error('❌ IoT token refresh error:', error);
        // If refresh fails, try fresh login
        this.clearToken();
        const response = await this.login(HARDCODED_CREDENTIALS);
        return response.data.token;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async getDevices(): Promise<IotDevicesResponse> {
    try {
      // Ensure we have a valid token
      const token = await this.ensureAuthenticated();

      const response = await axios.post<IotDevicesResponse>(
        `${IOT_BASE_URL}/device/acs/select/condition/page`,
        {},
        {
          headers: {
            token: token,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error('IoT get devices error:', error);

      // If unauthorized, try to refresh token and retry once
      if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
        console.log('🔄 Attempting to refresh IoT token and retry...');
        try {
          const newToken = await this.refreshToken();
          const response = await axios.post<IotDevicesResponse>(
            `${IOT_BASE_URL}/device/acs/select/condition/page`,
            {},
            {
              headers: {
                token: newToken,
              },
            },
          );
          return response.data;
        } catch (retryError) {
          console.error('IoT retry after refresh failed:', retryError);
          this.clearToken();
          throw retryError;
        }
      }

      throw error;
    }
  }

  async controlDoor(payload: IotDoorControlRequest): Promise<IotDoorControlResponse> {
    try {
      const token = await this.ensureAuthenticated();

      const response = await axios.post<IotDoorControlResponse>(
        `${IOT_BASE_URL}/device/acs/cmd/door/control`,
        payload,
        {
          headers: {
            token,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error('IoT door control error:', error);

      if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
        console.log('🔄 Attempting to refresh IoT token and retry door control...');
        try {
          const newToken = await this.refreshToken();
          const response = await axios.post<IotDoorControlResponse>(
            `${IOT_BASE_URL}/device/acs/cmd/door/control`,
            payload,
            {
              headers: {
                token: newToken,
              },
            },
          );
          return response.data;
        } catch (retryError) {
          console.error('IoT door control retry after refresh failed:', retryError);
          this.clearToken();
          throw retryError;
        }
      }

      throw error;
    }
  }

  logout(): void {
    console.log('🚪 Logging out from IoT service...');
    this.clearToken();
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export const iotApiService = new IotApiService();
